<template>
  <div ref="rootRef" class="l3">
    <a class="skip" href="#main">Saltar al contenido</a>
    <main id="main">
      <section class="hero wrap" aria-labelledby="hero-title">
        <div class="hero-copy">
          <div class="eyebrow">Prio / Proyectos</div>
          <h1 id="hero-title">
            El cliente<br />dijo que sí.<br />
            <span class="accent">¿Y ahora qué?</span>
          </h1>
          <p v-if="isCopyV2" class="lead">
            Ahora, que no dependa todo de tu cabeza. Tareas, cobros y fechas en su lugar. Y un link
            para que tu cliente siga el avance.
          </p>
          <p v-else class="lead">
            Ahora, que no dependa todo de tu cabeza. Tareas, cobros y fechas en su lugar. Y un link
            para que tu cliente siga el avance sin perseguirte.
          </p>
          <a class="cta" :href="SIGNUP_URL"
            >Probá gratis 30 días <span aria-hidden="true">↗</span>
          </a>
          <div class="guarantee">Sin tarjeta</div>
        </div>
        <div class="hero-art">
          <div id="hero-scene" class="scene" :class="{ running: heroRunning }">
            <div class="orbit" aria-hidden="true"></div>
            <span class="spark" aria-hidden="true">✳</span>
            <div class="hero-card">
              <div class="hero-surface"></div>
              <div class="approval-toast" aria-hidden="true">
                <span class="check">✓</span>
                <div>
                  <b>Carla aprobó el presupuesto.</b>
                  <small>Ese pequeño gran sí.</small>
                </div>
              </div>
              <div class="hero-project">
                <div class="mock-top">
                  <span>Tu espacio de trabajo</span>
                  <span class="pill paid">Activo</span>
                </div>
                <h2>Cumple de 15 · Carla</h2>
                <div class="project-progress">
                  <span>Proyecto en marcha</span>
                  <strong>35%</strong>
                </div>
                <div
                  class="bar"
                  style="--progress: 35%"
                  role="progressbar"
                  aria-label="Avance del proyecto de ejemplo"
                  aria-valuenow="35"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <i> </i>
                </div>
                <div class="project-metrics">
                  <div><b>12</b>tareas ordenadas</div>
                  <div><b>3</b>cobros a seguir</div>
                </div>
                <div class="project-footer">✓ Acuerdo listo. Una cosa menos.</div>
              </div>
            </div>
            <span class="hand-note" aria-hidden="true">El sí era el comienzo. ↗</span>
          </div>
          <div class="scene-caption">
            <span class="note">Ejemplo de proyecto</span>
            <button
              id="replay"
              class="text-button"
              type="button"
              data-action="hero-replay"
              @click="playHero"
            >
              Repetir el sí ↻
            </button>
          </div>
        </div>
      </section>
      <section class="pain wrap" aria-labelledby="pain-title">
        <div class="pain-intro reveal">
          <h2 id="pain-title">Ganaste el trabajo.<br />No otra jornada de mensajes.</h2>
          <p>
            El presupuesto se aprobó. El lío recién empieza si todo lo que sigue queda repartido por
            ahí.
          </p>
        </div>
        <div class="pain-grid">
          <div class="pain-item reveal">
            <div class="pain-number" data-count="12">12</div>
            <p>
              <strong>tareas en tu cabeza.</strong>
              <br />Y la sensación de que te estás olvidando de una.
            </p>
          </div>
          <div class="pain-item reveal">
            <div class="pain-number" data-count="3">3</div>
            <p>
              <strong>{{ isCopyV2 ? 'cobros para seguir.' : 'cobros que reclamar.' }}</strong>
              <br />A tiempo. Sin sentir que estás pidiendo un favor.
            </p>
          </div>
          <div class="pain-item reveal">
            <div class="pain-number" data-count="2">2</div>
            <p>
              <strong>documentos por escribir.</strong>
              <br />Para que no vuelva el “Yo pensé que incluía la edición”.
            </p>
          </div>
          <div class="pain-item reveal">
            <div class="pain-number" data-count="1">1</div>
            <p>
              <strong>cliente preguntando.</strong>
              <br />“¿Cómo vamos?” Cada tres días. Aunque todo vaya bien.
            </p>
          </div>
        </div>
        <p class="pain-end reveal">
          No necesitás acordarte de más. Necesitás dejar de sostenerlo todo de memoria.
        </p>
      </section>
      <section class="section flow" id="seguimiento" aria-labelledby="flow-title">
        <div class="wrap">
          <div class="eyebrow">{{ isCopyV2 ? 'Del presupuesto al proyecto en marcha' : 'Del presupuesto al trabajo de verdad' }}</div>
          <div class="flow-heading reveal">
            <h2 id="flow-title">
              El sí ya lo tenés.<br />
              <span class="accent">Ahora tené el plan.</span>
            </h2>
            <p>Sin arrancar de cero. Sin pasar la tarde armando el sistema para poder trabajar.</p>
          </div>
          <article class="beat">
            <div class="beat-copy reveal">
              <span class="beat-num">01 / ARRANCÁS</span>
              <h3 v-if="isCopyV2">Aprovechá lo que<br />ya cargaste.</h3>
              <h3 v-else>Un tap.<br />No otra carga de datos.</h3>
              <p>
                Convertís el presupuesto aprobado en proyecto. Cliente, ítems y total vienen con él.
                Lo que ya escribiste, no lo volvés a escribir.
              </p>
              <p class="aside">Del aprobado a las preguntas. Después revisás y se arma.</p>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span>Presupuesto / Carla</span>
                <span class="pill paid">Aprobado</span>
              </div>
              <div class="mock-title">Cumple de 15</div>
              <div class="row">
                <span>Cobertura fotográfica</span>
                <b>$ 960.000</b>
              </div>
              <div class="row">
                <span>Edición y entrega digital</span>
                <b>$ 400.000</b>
              </div>
              <div class="row">
                <span>Total aprobado</span>
                <strong class="quote-total">$ 1.360.000</strong>
              </div>
              <div class="inheritance">
                <span>✓ Carla</span>
                <span>✓ 2 ítems</span>
                <span>✓ Mismo total</span>
              </div>
              <button
                id="start-project"
                class="button ink full"
                type="button"
                data-action="project-start"
                @click="startProject"
              >
                Crear proyecto ↗
              </button>
              <p class="note" style="margin-top: 10px">
                Demo ilustrativa. Nada se envía ni se guarda.
              </p>
            </div>
          </article>
          <article class="beat" id="preguntas">
            <div class="beat-copy reveal">
              <span class="beat-num">02 / RESPONDÉS</span>
              <h3>Pregunta lo justo.<br />Y sobre lo tuyo.</h3>
              <p v-if="isCopyV2">
                La IA hace unas pocas preguntas para tu rubro. Al fotógrafo, horas de cobertura. Al
                jardinero, frecuencia. Elegís opciones y aclarás lo que haga falta.
              </p>
              <p v-else>
                La IA hace entre 3 y 5 preguntas para tu rubro. Al fotógrafo, horas de cobertura. Al
                jardinero, frecuencia. Elegís opciones y aclarás lo que haga falta.
              </p>
              <p class="aside">No le escribas un choclo de texto para que entienda tu trabajo.</p>
            </div>
            <div class="mock reveal" id="wizard">
              <div class="mock-top">
                <span>Armemos el proyecto / Fotografía</span>
                <span id="question-count" class="pill">{{ step + 1 }} de 3</span>
              </div>
              <div class="wizard-progress" aria-hidden="true">
                <i v-for="n in 3" :key="n" :class="{ on: n - 1 <= step }"> </i>
              </div>
              <fieldset>
                <legend id="question-title" ref="questionTitleRef" tabindex="-1">
                  {{ questions[step].title }}
                </legend>
                <div
                  id="wizard-options"
                  class="options"
                  role="group"
                  aria-labelledby="question-title"
                >
                  <button
                    v-for="opt in questions[step].options"
                    :key="opt"
                    type="button"
                    class="option"
                    data-action="wizard-option"
                    :data-context="opt"
                    :aria-pressed="opt === answers[step]"
                    @click="selectOption(opt)"
                  >
                    {{ opt }}
                  </button>
                </div>
              </fieldset>
              <label class="field-label" for="clarification"
                >Algo más que la IA tiene que saber</label
              >
              <textarea
                id="clarification"
                v-model="clarifications[step]"
                data-input="wizard-clarification"
                placeholder="Ej.: arrancamos con los preparativos."
              ></textarea>
              <div class="wizard-footer">
                <span id="wizard-summary" class="note" role="status">{{ wizardSummary }}</span>
                <button
                  id="wizard-next"
                  class="button"
                  type="button"
                  data-action="wizard-next"
                  @click="wizardNext"
                >
                  {{ finished ? 'Volver a probar' : step === 2 ? 'Ver resumen →' : 'Siguiente →' }}
                </button>
              </div>
            </div>
          </article>
          <article class="beat" id="plan">
            <div class="beat-copy reveal">
              <span class="beat-num">03 / REVISÁS</span>
              <h3>Qué falta hacer.<br />Qué falta cobrar.<br />Qué viene después.</h3>
              <p>
                Un checklist por etapas con avance en porcentaje. Un plan de cobros que cierra con
                el total. Una agenda con las fechas a mano. La IA propone; vos editás antes de dar
                el “dale”.
              </p>
              <p>
                ¿Apareció un adicional en pleno trabajo? Va como extra del proyecto, fuera del
                presupuesto. Notas y actividad guardan el hilo.
              </p>
              <p class="aside">
                Activo, pausado o terminado: el estado lo manejás vos, puertas adentro.
              </p>
            </div>
            <div class="mock plan-mock reveal">
              <div class="mock-top">
                <span>Tu plan / Vista de seguimiento</span>
                <span class="pill">Solo vos</span>
              </div>
              <div class="project-progress">
                <span>Checklist por etapas</span>
                <strong>65%</strong>
              </div>
              <div
                class="bar"
                role="progressbar"
                aria-label="Avance del checklist de ejemplo"
                aria-valuenow="65"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <i> </i>
              </div>
              <div class="plan-head">
                <strong>01 / Antes del evento</strong>
                <span class="mono">EDITABLE</span>
              </div>
              <div class="checkline"><span class="check done">✓</span>Definir la cobertura</div>
              <label class="field-label" for="plan-task">Próxima tarea · probá editarla</label>
              <input
                id="plan-task"
                v-model="planTask"
                class="edit-input"
                data-input="plan-task"
                @input="onPlanInput"
              />
              <div class="plan-head">
                <strong>02 / Entrega</strong>
              </div>
              <div class="checkline"><span class="check"> </span>Editar y entregar las fotos</div>
              <div class="plan-head">
                <strong>Cobros</strong>
              </div>
              <div class="row">
                <span>Seña · 40% <span class="pill paid">Pagado</span> </span>
                <b>$ 544.000</b>
              </div>
              <div class="row">
                <span>Saldo <span class="pill pending">Pendiente</span> </span>
                <b>$ 816.000</b>
              </div>
              <div class="plan-total">
                <span>Total del plan</span>
                <b>$ 1.360.000 ✓</b>
              </div>
              <div class="plan-head">
                <strong>Agenda</strong>
              </div>
              <div class="note">Evento · Salón Los Álamos · Vie 13/03</div>
              <div class="internal">
                <div>
                  <strong>Extras + notas</strong>
                  <p>Hora adicional solicitada.<br />Fuera del presupuesto.</p>
                </div>
                <div>
                  <strong>Actividad</strong>
                  <p>Se registró la seña.<br />Se actualizó la agenda.</p>
                </div>
              </div>
              <button
                id="apply-plan"
                class="button ink full"
                type="button"
                data-action="plan-apply"
                :disabled="planApplied"
                @click="applyPlan"
              >
                {{ planApplied ? '✓ Sugerencias aplicadas' : 'Aplicar sugerencias' }}
              </button>
              <p id="plan-status" class="note" role="status" style="margin-top: 10px">
                {{ planStatus }}
              </p>
            </div>
          </article>
          <article class="beat">
            <div class="beat-copy reveal">
              <span class="beat-num">04 / DEJÁS TODO CLARO</span>
              <h3>Los papeles se escriben.<br />Tu voz se queda.</h3>
              <p>
                Acuerdo y guía con tu tono y tu marca. ¿No suena a vos? Pedile “hacelo más formal” y
                lo reescribe. Cada versión queda guardada. Volvés a una anterior cuando quieras.
              </p>
              <p class="aside">Menos redactar condiciones. Menos aclararlas después.</p>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span>Acuerdo + guía / Documentos</span>
                <span class="pill">Tu marca</span>
              </div>
              <div class="doc-paper">
                <div class="doc-brand">olivia foto<span>ACUERDO / CARLA</span></div>
                <h4>Para disfrutar,<br />sin malentendidos.</h4>
                <p id="doc-copy">{{ versions[selectedVersion - 1] }}</p>
                <footer>
                  <span>ALCANCE Y CONDICIONES</span>
                  <span id="doc-version">{{ 'VERSIÓN ' + selectedVersion }}</span>
                </footer>
              </div>
              <div class="versions" role="group" aria-label="Versiones guardadas">
                <button
                  v-for="n in versions.length"
                  :key="n"
                  type="button"
                  data-action="doc-version"
                  :data-context="'v' + n"
                  :data-version="String(n)"
                  :aria-pressed="selectedVersion === n"
                  @click="onVersion(n)"
                >
                  v{{ n }}
                </button>
                <button
                  id="restore-version"
                  type="button"
                  class="restore"
                  data-action="doc-restore"
                  @click="restoreVersion"
                >
                  Restaurar versión
                </button>
              </div>
              <div class="rewrite">
                <span>“Hacelo más formal”</span>
                <button
                  id="rewrite-doc"
                  type="button"
                  data-action="doc-rewrite"
                  :disabled="rewriteDone"
                  @click="rewriteDoc"
                >
                  {{ rewriteDone ? '✓ Reescrito' : 'Probar ↗' }}
                </button>
              </div>
              <p id="doc-status" class="note document-note" role="status">{{ docStatus }}</p>
            </div>
          </article>
        </div>
      </section>
      <section class="portal-section" id="portal" aria-labelledby="portal-title">
        <div class="wrap">
          <div class="section-head portal-heading reveal">
            <div class="eyebrow">Tu cliente al tanto. Vos en lo tuyo.</div>
            <h2 v-if="isCopyV2" id="portal-title">
              Tu cliente puede ver<br />cómo avanza
              <span class="accent">el trabajo.</span>
            </h2>
            <h2 v-else id="portal-title">
              Se acabó el<br />“¿cómo venimos?”.<br />
              <span class="accent">El cliente lo ve solo.</span>
            </h2>
            <p class="lead">
              Compartís un link con PIN. Carla entra, ve el avance y encuentra lo que necesita. No
              te pide un resumen. No le mandás cinco capturas.
            </p>
          </div>
          <div class="portal-demo">
            <div class="controls reveal">
              <div class="eyebrow">01 / Esto lo decidís vos</div>
              <h3>Tu proyecto.<br />No todo tu detrás de escena.</h3>
              <p>Prendé y apagá las secciones. Lo que cambia acá, cambia en el link del cliente.</p>
              <label class="control-label" for="toggle-tasks">
                <span>
                  <strong>Tareas</strong>
                  <small>Lo hecho y lo que falta</small>
                </span>
                <span class="switch">
                  <input
                    id="toggle-tasks"
                    type="checkbox"
                    data-action="portal-section"
                    data-context="tasks"
                    :checked="showTasks"
                    data-section="client-tasks"
                    aria-controls="client-tasks"
                    @change="onSection($event, 'tasks')"
                  />
                  <i aria-hidden="true"> </i>
                </span>
              </label>
              <label class="control-label" for="toggle-agenda">
                <span>
                  <strong>Agenda</strong>
                  <small>Fecha y lugar, a mano</small>
                </span>
                <span class="switch">
                  <input
                    id="toggle-agenda"
                    type="checkbox"
                    data-action="portal-section"
                    data-context="agenda"
                    :checked="showAgenda"
                    data-section="client-agenda"
                    aria-controls="client-agenda"
                    @change="onSection($event, 'agenda')"
                  />
                  <i aria-hidden="true"> </i>
                </span>
              </label>
              <label class="control-label" for="toggle-payments">
                <span>
                  <strong>Pagos</strong>
                  <small>Qué pagó y qué queda</small>
                </span>
                <span class="switch">
                  <input
                    id="toggle-payments"
                    type="checkbox"
                    data-action="portal-section"
                    data-context="payments"
                    :checked="showPayments"
                    data-section="client-payments"
                    aria-controls="client-payments"
                    @change="onSection($event, 'payments')"
                  />
                  <i aria-hidden="true"> </i>
                </span>
              </label>
              <label class="control-label" for="toggle-documents">
                <span>
                  <strong>Documentos</strong>
                  <small>Solo los que vos elijas</small>
                </span>
                <span class="switch">
                  <input
                    id="toggle-documents"
                    type="checkbox"
                    data-action="portal-section"
                    data-context="documents"
                    :checked="showDocuments"
                    data-section="client-documents"
                    aria-controls="client-documents"
                    @change="onSection($event, 'documents')"
                  />
                  <i aria-hidden="true"> </i>
                </span>
              </label>
              <div class="per-doc">
                <label>
                  <input
                    type="checkbox"
                    data-action="portal-document"
                    data-context="agreement"
                    :checked="docAgreement"
                    :disabled="!showDocuments"
                    data-document="pdf-agreement"
                    @change="onDocument($event, 'agreement')"
                  />Acuerdo de cobertura</label
                >
                <label>
                  <input
                    type="checkbox"
                    data-action="portal-document"
                    data-context="guide"
                    :checked="docGuide"
                    :disabled="!showDocuments"
                    data-document="pdf-guide"
                    @change="onDocument($event, 'guide')"
                  />Guía del evento</label
                >
              </div>
              <div
                id="portal-status"
                class="control-status"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {{ portalStatus }}
              </div>
              <div class="pin-explainer">
                <strong>Un link. Cuatro dígitos. Nada que instalar.</strong>
                <p>
                  Entra con el PIN <b>4821</b>, sin cuenta ni app. Con “Recordarme”,
                  {{ isCopyV2 ? 'puede recordar el acceso durante 7 días.' : 'una cookie conserva el acceso durante 7 días.' }}
                </p>
              </div>
            </div>
            <div class="preview-stage reveal">
              <div class="preview-label mono">
                <span>02 / Esto ve Carla</span>
                <span class="live-dot">VISTA EN VIVO</span>
              </div>
              <div class="browser" aria-label="Vista previa del portal público del cliente">
                <div class="address">
                  <svg viewBox="0 0 12 14" fill="none" stroke="currentColor" aria-hidden="true">
                    <rect x="1" y="6" width="10" height="7" rx="2" />
                    <path d="M3 6V4a3 3 0 0 1 6 0v2" />
                  </svg>
                  <span>/u/tu-negocio/proyecto/8F3K</span>
                </div>
                <div class="portal-body">
                  <header>
                    <div class="client-brand">olivia foto<span class="pill">PIN 4821</span></div>
                    <div class="client-header">
                      <p>CARLA / TU PROYECTO</p>
                      <h3>Tu cumple de 15.<br />Cada vez más cerca.</h3>
                      <div class="project-progress">
                        <span>Así venimos</span>
                        <strong>65%</strong>
                      </div>
                      <div
                        class="bar"
                        role="progressbar"
                        aria-label="Avance general que ve Carla"
                        aria-valuenow="65"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        <i> </i>
                      </div>
                    </div>
                  </header>
                  <section
                    id="client-tasks"
                    class="portal-block"
                    aria-labelledby="client-tasks-title"
                    :hidden="!showTasks"
                  >
                    <h4 id="client-tasks-title">Tareas</h4>
                    <div class="task-group">Antes del evento</div>
                    <div class="checkline">
                      <span class="check done" aria-label="Hecha">✓</span>Definir la cobertura
                    </div>
                    <div class="checkline">
                      <span class="check done" aria-label="Hecha">✓</span>Coordinar con el salón
                    </div>
                    <div class="checkline">
                      <span class="check" aria-label="Pendiente"> </span>Confirmar horarios
                    </div>
                    <div class="task-group">Entrega</div>
                    <div class="checkline">
                      <span class="check" aria-label="Pendiente"> </span>Editar y entregar las fotos
                    </div>
                    <p class="note">Algunas tareas de tu proyecto.</p>
                  </section>
                  <section
                    id="client-agenda"
                    class="portal-block"
                    aria-labelledby="client-agenda-title"
                    :hidden="!showAgenda"
                  >
                    <h4 id="client-agenda-title">Agenda</h4>
                    <div class="event">
                      <div class="event-date">VIE<b>13</b>MARZO</div>
                      <div>
                        <strong>Evento · Vie 13/03</strong>
                        <p>Salón Los Álamos</p>
                      </div>
                    </div>
                  </section>
                  <section
                    id="client-payments"
                    class="portal-block"
                    aria-labelledby="client-payments-title"
                    :hidden="!showPayments"
                  >
                    <h4 id="client-payments-title">Pagos</h4>
                    <div class="payment-row">
                      <div>
                        <small>Seña · 40%</small>
                        <strong>$ 544.000</strong>
                      </div>
                      <span class="pill paid">Pagado</span>
                    </div>
                    <div class="payment-row">
                      <div>
                        <small>Saldo</small>
                        <strong>$ 816.000</strong>
                      </div>
                      <span class="pill pending">Pendiente</span>
                    </div>
                    <div class="payment-total">Total del proyecto <b>$ 1.360.000</b></div>
                  </section>
                  <section
                    id="client-documents"
                    class="portal-block"
                    aria-labelledby="client-documents-title"
                    :hidden="!showDocuments"
                  >
                    <h4 id="client-documents-title">Documentos</h4>
                    <div id="pdf-agreement" class="pdf" :hidden="!docAgreement">
                      <span class="pdf-icon">PDF</span>
                      <span class="pdf-name">Acuerdo de cobertura</span>
                      <a
                        class="download"
                        data-action="portal-download"
                        data-context="agreement"
                        data-pdf="agreement"
                        download="acuerdo-demo.pdf"
                        :href="pdfAgreement ?? undefined"
                        aria-label="Descargar Acuerdo de cobertura, PDF de ejemplo"
                        >↓</a
                      >
                    </div>
                    <div id="pdf-guide" class="pdf" :hidden="!docGuide">
                      <span class="pdf-icon">PDF</span>
                      <span class="pdf-name">Guía del evento</span>
                      <a
                        class="download"
                        data-action="portal-download"
                        data-context="guide"
                        data-pdf="guide"
                        download="guia-demo.pdf"
                        :href="pdfGuide ?? undefined"
                        aria-label="Descargar Guía del evento, PDF de ejemplo"
                        >↓</a
                      >
                    </div>
                    <p id="no-documents" class="note" :hidden="docAgreement || docGuide">
                      No hay documentos compartidos.
                    </p>
                  </section>
                  <div id="all-off" class="empty-state" :hidden="sectionCount > 0">
                    <span class="accent" aria-hidden="true">◎</span>
                    <strong>Solo avance general.</strong>
                    <p>El progreso sigue a la vista.<br />Los detalles quedan de tu lado.</p>
                  </div>
                  <div class="client-footer">Tu proyecto, cerca. Con <b>prio.</b></div>
                </div>
              </div>
              <p class="preview-caption">
                Demo abierta para probar. Los PDF son ejemplos.<br />En tu portal real, el cliente
                entra con su PIN.
              </p>
            </div>
          </div>
          <div class="portal-bottom reveal">
            <div>
              <strong>Si lo apagás, desaparece.</strong>
              <p>
                Revocás el acceso a esa sección o documento al instante. Compartir no es una
                decisión para siempre.
              </p>
            </div>
            <div>
              <strong>Tu cocina no se comparte.</strong>
              <p>
                Los estados internos del proyecto y de los documentos nunca se muestran. Carla ve el
                avance, no tus borradores.
              </p>
            </div>
            <div>
              <strong>Hecho para abrir desde el celu.</strong>
              <p v-if="isCopyV2">
                Tu cliente lo abre desde el celular, sin instalar nada. Cinco secciones: avance
                general, tareas, agenda, pagos y documentos.
              </p>
              <p v-else>
                Un portal servido desde el servidor, pensado primero para móvil. Cinco secciones:
                avance general, tareas, agenda, pagos y documentos.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section class="trust wrap" id="control" aria-labelledby="trust-title">
        <div class="trust-box reveal">
          <div>
            <div class="eyebrow">Ayuda, no piloto automático</div>
            <h2 id="trust-title">
              La IA sugiere.<br />
              <span class="accent">Vos tenés la<br />última palabra.</span>
            </h2>
          </div>
          <ul class="trust-list">
            <li>
              <span>01</span>
              <div>
                <strong>El plan se revisa antes de aplicar.</strong>
                <p>Tareas, cobros y agenda editables. Nada se aplica sin tu “dale”.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>Los documentos se corrigen por mensaje.</strong>
                <p>Pedís el cambio. Revisás. Y si preferís la anterior, la restaurás.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>El link muestra lo que vos decidís.</strong>
                <p>
                  Interruptor por interruptor. Documento por documento. Sin exponer estados
                  internos.
                </p>
              </div>
            </li>
          </ul>
        </div>
      </section>
      <section class="closing" aria-labelledby="closing-title">
        <span class="closing-spark" aria-hidden="true">✳</span>
        <div class="wrap reveal">
          <div class="eyebrow">Menos perseguir. Más hacer.</div>
          <h2 id="closing-title">
            Que el próximo sí<br />te encuentre<br />
            <span>con todo armado.</span>
          </h2>
          <p>Vos seguís el trabajo. Tu cliente sigue el avance.</p>
          <a class="cta" :href="SIGNUP_URL">Probá gratis <span aria-hidden="true">↗</span> </a>
          <div class="guarantee">Sin tarjeta · Cancelás cuando quieras</div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useHead } from '@unhead/vue';
import { useScrollReveal } from '@/composables/useScrollReveal';
import { useCopyVariant } from '@/composables/useCopyVariant';
import { SIGNUP_URL } from '@/constants';

const { isCopyV2 } = useCopyVariant();

useHead({
  title: 'Prio - Del sí al proyecto armado',
  meta: [
    {
      name: 'description',
      content:
        'Del presupuesto aprobado al proyecto: checklist, cobros, agenda y documentos con IA. Tu cliente sigue el avance por link con PIN.',
    },
    { name: 'theme-color', content: '#f7f3fb' },
  ],
  link: [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Urbanist:wght@500;600;700;800;900&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap',
    },
  ],
});

const rootRef = ref<HTMLElement | null>(null);
const reducedMotion = ref(false);
let disposed = false;
let mediaQuery: MediaQueryList | null = null;
let heroTimer = 0;
let heroRafId = 0;

// Hero approval-toast to project morph replay (illustrative demo).
const heroRunning = ref(false);

function playHero(): void {
  window.clearTimeout(heroTimer);
  cancelAnimationFrame(heroRafId);
  heroRunning.value = false;
  if (reducedMotion.value || disposed) return;
  heroRafId = requestAnimationFrame(() => {
    if (disposed) return;
    heroRafId = requestAnimationFrame(() => {
      if (reducedMotion.value || disposed) return;
      heroRunning.value = true;
      heroTimer = window.setTimeout(() => {
        heroRunning.value = false;
      }, 1950);
    });
  });
}

// Wizard: 3 trade questions (local demo, nothing is generated).
interface WizardQuestion {
  title: string;
  options: string[];
}

const questions: WizardQuestion[] = [
  { title: '¿Cuántas horas de cobertura?', options: ['4 horas', '6 horas', '8 horas'] },
  {
    title: '¿Qué momentos cubrís?',
    options: ['Solo la fiesta', 'Preparativos + fiesta', 'Todo el día'],
  },
  {
    title: '¿Cómo entregás las fotos?',
    options: ['Entrega digital', 'Álbum impreso', 'Digital + álbum'],
  },
];
const answers = ref<string[]>(['6 horas', 'Preparativos + fiesta', 'Entrega digital']);
const clarifications = ref<string[]>(['', '', '']);
const step = ref(0);
const finished = ref(false);
const wizardSummary = ref('Elegí una opción. Podés cambiarla.');
const questionTitleRef = ref<HTMLElement | null>(null);

function selectOption(option: string): void {
  answers.value[step.value] = option;
  wizardSummary.value = 'Elegiste: ' + option + '.';
}

function renderQuestion(focus: boolean): void {
  wizardSummary.value = 'Elegiste: ' + answers.value[step.value] + '.';
  if (focus) questionTitleRef.value?.focus({ preventScroll: true });
}

function wizardNext(): void {
  if (finished.value) {
    step.value = 0;
    finished.value = false;
    renderQuestion(true);
    return;
  }
  if (step.value < 2) {
    step.value += 1;
    renderQuestion(true);
    return;
  }
  finished.value = true;
  wizardSummary.value =
    answers.value.join(' · ') +
    '. Respuestas listas para revisar. Demo: no se genera un plan nuevo.';
}

function startProject(): void {
  step.value = 0;
  finished.value = false;
  wizardSummary.value = 'Carla, los 2 ítems y $ 1.360.000 ya están. Respondé lo que falta.';
  document
    .getElementById('preguntas')
    ?.scrollIntoView({ behavior: reducedMotion.value ? 'auto' : 'smooth', block: 'start' });
  questionTitleRef.value?.focus({ preventScroll: true });
}

// Plan review (demo: nothing is actually applied).
const planTask = ref('Confirmar horarios con Carla');
const planApplied = ref(false);
const planStatus = ref('Ejemplo de seguimiento. Nada se aplica sin tu aprobación.');

function applyPlan(): void {
  const task = planTask.value.trim();
  if (!task) {
    planStatus.value = 'Escribí la próxima tarea antes de aplicar.';
    document.getElementById('plan-task')?.focus();
    return;
  }
  planStatus.value =
    'Aplicado en esta demo. Próxima tarea: ' + task + '. No se guardó ni se compartió nada.';
  planApplied.value = true;
}

function onPlanInput(): void {
  planApplied.value = false;
  planStatus.value = 'Cambios por revisar. Todavía no se aplicaron.';
}

// Versioned documents (local demo).
const versions = ref<string[]>([
  'Carla, este acuerdo incluye la cobertura fotográfica de tu cumple y la entrega de las fotos editadas en formato digital.',
  'Carla, cubrimos tu cumple durante 6 horas y te entregamos las fotos editadas en formato digital. Dejamos el alcance por escrito para que sepas qué está incluido.',
  'Carla, vamos a cubrir tu cumple durante 6 horas. Después editamos las fotos y te las entregamos en formato digital. La seña es del 40%; el resto queda como saldo.',
]);
const selectedVersion = ref(3);
const currentVersion = ref(3);
const rewriteDone = ref(false);
const docStatus = ref(
  'Elegí una versión y restaurala. Demo de acuerdo; la guía también se versiona.',
);

function showVersion(version: number): void {
  selectedVersion.value = version;
}

function onVersion(version: number): void {
  showVersion(version);
  docStatus.value = 'Viendo v' + version + '. La versión actual es v' + currentVersion.value + '.';
}

function restoreVersion(): void {
  currentVersion.value = selectedVersion.value;
  docStatus.value =
    'v' + currentVersion.value + ' restaurada en esta demo. Las demás versiones siguen guardadas.';
}

function rewriteDoc(): void {
  versions.value.push(
    'El servicio comprende 6 horas de cobertura fotográfica del evento y la posterior edición y entrega digital de las imágenes. Se establece una seña del 40% del total aprobado; el importe restante corresponde al saldo.',
  );
  currentVersion.value = versions.value.length;
  showVersion(currentVersion.value);
  docStatus.value =
    'Ejemplo de tono formal guardado como v' +
    currentVersion.value +
    '. Podés restaurar cualquiera de las anteriores.';
  rewriteDone.value = true;
}

// Client portal toggles (demo: switching off removes it from the link instantly).
const showTasks = ref(true);
const showAgenda = ref(true);
const showPayments = ref(true);
const showDocuments = ref(true);
const docAgreement = ref(true);
const docGuide = ref(true);
const portalStatus = ref('4 secciones compartidas. Probá apagar una.');

const sectionCount = computed<number>(
  () =>
    [showTasks.value, showAgenda.value, showPayments.value, showDocuments.value].filter(Boolean)
      .length,
);

function refreshPortal(lastChange?: { kind: 'section' | 'document'; on: boolean }): void {
  const count = sectionCount.value;
  if (count === 0) {
    portalStatus.value = 'Solo avance general. Ningún detalle compartido.';
    return;
  }
  let message = count + ' ' + (count === 1 ? 'sección compartida.' : 'secciones compartidas.');
  if (lastChange?.kind === 'section' && !lastChange.on) {
    message += ' Lo que apagaste desapareció del link al instante.';
  } else if (lastChange?.kind === 'document') {
    message += lastChange.on
      ? ' Documento visible para Carla.'
      : ' Documento retirado del link al instante.';
  } else {
    message += ' Carla ve lo que elegiste.';
  }
  portalStatus.value = message;
}

function onSection(event: Event, key: 'tasks' | 'agenda' | 'payments' | 'documents'): void {
  const checked = (event.target as HTMLInputElement).checked;
  if (key === 'tasks') showTasks.value = checked;
  if (key === 'agenda') showAgenda.value = checked;
  if (key === 'payments') showPayments.value = checked;
  if (key === 'documents') showDocuments.value = checked;
  refreshPortal({ kind: 'section', on: checked });
}

function onDocument(event: Event, key: 'agreement' | 'guide'): void {
  const checked = (event.target as HTMLInputElement).checked;
  if (key === 'agreement') docAgreement.value = checked;
  else docGuide.value = checked;
  refreshPortal({ kind: 'document', on: checked });
}

// Sample PDFs stay local (no API, no real client data).
const pdfAgreement = ref<string | null>(null);
const pdfGuide = ref<string | null>(null);
const pdfUrls: string[] = [];

function buildPdfUrl(lines: string[]): string {
  const body =
    'BT /F1 13 Tf 50 780 Td ' +
    lines
      .map(
        (text, index) =>
          (index ? '0 -28 Td ' : '') + '(' + text.replace(/[\\()]/g, '\\$&') + ') Tj',
      )
      .join('\n') +
    ' ET';
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Length ' + body.length + ' >>\nstream\n' + body + '\nendstream',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += index + 1 + ' 0 obj\n' + object + '\nendobj\n';
  });
  const xref = pdf.length;
  pdf +=
    'xref\n0 6\n0000000000 65535 f \n' +
    offsets
      .slice(1)
      .map((offset) => String(offset).padStart(10, '0') + ' 00000 n \n')
      .join('') +
    'trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n' +
    xref +
    '\n%%EOF';
  const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
  pdfUrls.push(url);
  return url;
}

// Pain count-ups (client-only, honors reduced motion).
let countObserver: IntersectionObserver | null = null;
const countRafIds = new Set<number>();

function animateCount(el: HTMLElement, end: number): void {
  const start = performance.now();
  const tick = (now: number): void => {
    if (disposed) return;
    const progress = Math.min(1, (now - start) / 650);
    el.textContent = String(Math.round(end * (1 - Math.pow(1 - progress, 3))));
    if (progress >= 1 || reducedMotion.value) {
      el.textContent = String(end);
      return;
    }
    countRafIds.add(requestAnimationFrame(tick));
  };
  countRafIds.add(requestAnimationFrame(tick));
}

function onMotionChange(): void {
  reducedMotion.value = mediaQuery?.matches ?? false;
  if (reducedMotion.value) {
    window.clearTimeout(heroTimer);
    heroRunning.value = false;
  }
}

onMounted(() => {
  mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  reducedMotion.value = mediaQuery.matches;
  mediaQuery.addEventListener('change', onMotionChange);
  playHero();
  pdfAgreement.value = buildPdfUrl([
    'PRIO / DOCUMENTO DE EJEMPLO',
    'Acuerdo de cobertura',
    'Cumple de 15 - Carla',
    'Cobertura fotografica y entrega digital.',
    'Total: $1.360.000. Sena 40%: $544.000.',
    'Saldo: $816.000.',
    'Demo ilustrativa. No es un acuerdo real.',
  ]);
  pdfGuide.value = buildPdfUrl([
    'PRIO / DOCUMENTO DE EJEMPLO',
    'Guia del evento',
    'Cumple de 15 - Carla',
    'Evento: viernes 13/03.',
    'Lugar: Salon Los Alamos.',
    'Confirmar horarios antes del evento.',
    'Demo ilustrativa. No es un documento real.',
  ]);
  if (!('IntersectionObserver' in window)) return;
  countObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const number = entry.target.querySelector('[data-count]');
        if (number instanceof HTMLElement && !reducedMotion.value) {
          animateCount(number, Number(number.dataset.count));
        }
        countObserver?.unobserve(entry.target);
      });
    },
    { threshold: 0.08 },
  );
  rootRef.value?.querySelectorAll('.reveal').forEach((el) => countObserver?.observe(el));
});

onUnmounted(() => {
  disposed = true;
  window.clearTimeout(heroTimer);
  cancelAnimationFrame(heroRafId);
  countObserver?.disconnect();
  mediaQuery?.removeEventListener('change', onMotionChange);
  countRafIds.forEach((id) => cancelAnimationFrame(id));
  countRafIds.clear();
  pdfUrls.forEach((url) => URL.revokeObjectURL(url));
});

useScrollReveal();
</script>

<style scoped>
h1,
h2,
h3,
h4 {
  font-family: Urbanist, Arial, sans-serif;
  letter-spacing: -0.045em;
  line-height: 1.04;
  font-weight: 800;
}
h1 {
  font-size: clamp(53px, 7vw, 92px);
}
h2 {
  font-size: clamp(40px, 5vw, 66px);
}
h3 {
  font-size: clamp(30px, 3vw, 40px);
}

a {
  color: inherit;
  text-decoration: none;
}
button,
input,
textarea {
  font: inherit;
  color: inherit;
}
button {
  cursor: pointer;
}
button,
a,
input,
textarea {
  -webkit-tap-highlight-color: transparent;
}
button,
a {
  touch-action: manipulation;
}
button,
a:not(.skip) {
  min-height: 44px;
}
a {
  display: inline-flex;
  align-items: center;
}
button {
  border: 0;
}
button:disabled {
  cursor: default;
}
button:focus-visible,
a:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 3px solid var(--violet);
  outline-offset: 4px;
}
[hidden] {
  display: none !important;
}
.wrap {
  width: min(1160px, calc(100% - 40px));
  margin: auto;
}
.mono,
.eyebrow {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.07em;
}
.eyebrow {
  text-transform: uppercase;
  color: var(--violet);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
}
.eyebrow:before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--pink);
  flex: none;
}

.cta {
  display: inline-flex;
  justify-content: space-between;
  gap: 32px;
  align-items: center;
  min-height: 56px !important;
  padding: 14px 24px;
  border: 1px solid #c9aee0;
  border-radius: 16px;
  background: var(--aurora);
  font-size: 13px;
  font-weight: 700;
  transition:
    transform 0.2s var(--ease),
    box-shadow 0.2s;
}
.cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px #6541bd1a;
}
.cta:active {
  transform: scale(0.98);
}
.cta span {
  font-size: 21px;
  font-weight: 400;
}
.guarantee {
  font-size: 10px;
  color: var(--muted);
  margin-top: 12px;
}

.section {
  padding: 90px 0;
}
.lead {
  font-size: 16px;
  line-height: 1.85;
}
.section-head {
  max-width: 720px;
  margin-bottom: 45px;
}
.section-head p {
  max-width: 520px;
  margin-top: 22px;
}
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 0;
  border-bottom: 1px solid var(--line);
  font-size: 12px;
}
.row:last-child {
  border-bottom: 0;
}
.row > span,
.row > div {
  min-width: 0;
}
.row b {
  font-weight: 600;
}
.pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 4px 9px;
  border-radius: 30px;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.6;
  background: #eee8f6;
  color: var(--violet);
}
.paid {
  color: #166344;
  background: #e5f5de;
}
.pending {
  color: #815010;
  background: #fff0d3;
}
.mock {
  border: 1px solid #ded4e8;
  border-radius: 24px;
  background: #fffcff;
  padding: 25px;
  box-shadow: 0 14px 35px #31204707;
  min-width: 0;
}
.mock-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 21px;
  border-bottom: 1px solid var(--line);
  font-size: 10px;
  color: var(--muted);
}
.mock-title {
  font:
    800 27px/1.07 Urbanist,
    sans-serif;
  letter-spacing: -0.035em;
  margin-bottom: 20px;
}
.note {
  font-size: 10px;
  color: var(--muted);
  line-height: 1.7;
}
.bar {
  height: 7px;
  overflow: hidden;
  border-radius: 20px;
  background: #eee7f4;
}
.bar i {
  display: block;
  height: 100%;
  width: var(--progress, 65%);
  background: var(--aurora);
  border-radius: inherit;
  transform-origin: left;
}
.button {
  min-height: 44px;
  background: #eee6f8;
  border: 1px solid #d8cae9;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 600;
}
.button:hover {
  background: #e6daf5;
}
.button.ink {
  background: var(--ink);
  border-color: var(--ink);
  color: #fff7ff;
}
.full {
  width: 100%;
  margin-top: 18px;
}
.check {
  display: inline-grid;
  place-items: center;
  width: 19px;
  height: 19px;
  border: 1px solid #c8bbd7;
  border-radius: 6px;
  font-size: 11px;
  flex: none;
}
.check.done {
  background: #e5f5de;
  color: #166344;
  border-color: #d2e8c8;
}
.checkline {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  padding: 10px 0;
}
.soft-rule {
  height: 1px;
  background: var(--line);
  margin: 22px 0;
}
.tiny-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
/* A single surface expands from the approval toast into the project. */
.hero {
  display: grid;
  grid-template-columns: 1.03fr 1fr;
  gap: 55px;
  align-items: center;
  padding: 55px 0 70px;
}
.hero-copy .lead {
  max-width: 440px;
  margin: 28px 0;
}
.hero-art {
  position: relative;
  min-width: 0;
  padding: 25px 0;
}
.scene {
  height: 420px;
  position: relative;
  display: grid;
  place-items: center;
  isolation: isolate;
}
.scene:before {
  content: '';
  position: absolute;
  inset: 25px -8px 10px;
  background: var(--aurora);
  border-radius: 48% 52% 39% 61% / 46% 36% 64% 54%;
  transform: rotate(-8deg);
  z-index: -2;
  opacity: 0.75;
}
.orbit {
  position: absolute;
  inset: 0 -10px;
  border: 1px solid #bfa9d5;
  border-radius: 50%;
  transform: rotate(-23deg);
  z-index: -1;
}
.spark {
  font:
    100px/1 Urbanist,
    sans-serif;
  position: absolute;
  right: -12px;
  top: -8px;
  color: var(--violet);
  transform: rotate(15deg);
}
.hero-card {
  position: relative;
  width: 86%;
  height: 328px;
  isolation: isolate;
}
.hero-surface {
  position: absolute;
  inset: 0;
  background: #fffcff;
  border: 1px solid #e6d9ee;
  border-radius: 24px;
  box-shadow: 0 25px 50px #4327671a;
  z-index: -1;
}
.hero-project {
  padding: 24px;
}
.hero-project h2 {
  font-size: 32px;
  margin: 22px 0;
}
.hero-project .mock-top {
  margin: 0;
  padding-bottom: 14px;
}
.project-progress {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 9px;
  font-size: 11px;
  color: var(--muted);
}
.project-progress strong {
  font:
    800 36px/1 Urbanist,
    sans-serif;
  color: var(--violet);
  letter-spacing: -2px;
}
.project-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 19px;
  gap: 12px;
  font-size: 11px;
}
.project-metrics b {
  display: block;
  font-size: 18px;
  line-height: 1.4;
}
.project-footer {
  margin-top: 18px;
  font-size: 11px;
  color: #166344;
}
.approval-toast {
  display: none;
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
  gap: 14px;
  pointer-events: none;
  font-size: 13px;
}
.approval-toast .check {
  width: 32px;
  height: 32px;
  background: var(--lime);
  border: 0;
}
.approval-toast small {
  display: block;
  color: var(--muted);
  font-size: 10px;
}
.running .hero-surface {
  animation: morph 1.9s var(--ease) both;
}
.running .hero-project {
  animation: project-in 1.9s var(--ease) both;
}
.running .approval-toast {
  display: flex;
  animation: toast-out 1.9s var(--ease) both;
}
.scene-caption {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 9px;
}
.text-button {
  padding: 8px;
  background: none;
  font-size: 10px;
  text-decoration: underline;
  text-underline-offset: 4px;
}
.hand-note {
  position: absolute;
  bottom: 0;
  right: 0;
  font:
    600 17px Urbanist,
    sans-serif;
  color: var(--violet);
  transform: rotate(-6deg);
}
@keyframes morph {
  0%,
  30% {
    transform: scale(0.91, 0.25);
  }
  75%,
  100% {
    transform: scale(1);
  }
}
@keyframes project-in {
  0%,
  42% {
    opacity: 0;
    transform: translateY(12px);
  }
  85%,
  100% {
    opacity: 1;
    transform: none;
  }
}
@keyframes toast-out {
  0%,
  28% {
    opacity: 1;
  }
  45%,
  100% {
    opacity: 0;
    transform: translateY(-15px);
  }
}
.pain {
  border-top: 1px solid var(--line);
  padding-top: 48px;
  padding-bottom: 70px;
}
.pain-intro {
  display: flex;
  justify-content: space-between;
  gap: 30px;
  align-items: baseline;
  margin-bottom: 36px;
}
.pain-intro h2 {
  font-size: 32px;
  max-width: 430px;
}
.pain-intro p {
  max-width: 350px;
  font-size: 12px;
}
.pain-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
}
.pain-item {
  border-top: 1px solid var(--line);
  padding-top: 12px;
}
.pain-number {
  font:
    600 90px/1 Urbanist,
    sans-serif;
  letter-spacing: -6px;
  color: #b59bcf;
}
.pain-item:last-child .pain-number {
  color: var(--violet);
}
.pain-item p {
  font-size: 12px;
  margin-top: 16px;
  max-width: 210px;
}
.pain-item strong {
  color: var(--ink);
  font-weight: 600;
}
.pain-end {
  margin-top: 34px;
  font-size: 13px;
  color: var(--ink);
}
.flow {
  background: #efe9f5;
  border-top: 1px solid #e8deef;
  border-bottom: 1px solid #e8deef;
}
.flow-heading {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 40px;
  margin-bottom: 60px;
}
.flow-heading h2 {
  max-width: 570px;
}
.flow-heading p {
  max-width: 280px;
  font-size: 13px;
}
.beat {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: center;
  padding: 45px 0;
  border-top: 1px solid #dcd0e6;
}
.beat-copy {
  max-width: 420px;
}
.beat-num {
  font:
    500 12px 'IBM Plex Mono',
    monospace;
  color: var(--violet);
  display: block;
  margin-bottom: 21px;
}
.beat-copy p {
  margin-top: 22px;
  font-size: 14px;
  line-height: 1.9;
}
.beat-copy .aside {
  font-size: 12px;
  color: var(--ink);
  padding-left: 15px;
  border-left: 2px solid var(--pink);
}
.inheritance {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  margin-top: 18px;
}
.inheritance span {
  font-size: 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  padding: 5px 9px;
}
.quote-total {
  font:
    800 31px Urbanist,
    sans-serif;
  letter-spacing: -1px;
}
.wizard-progress {
  display: flex;
  gap: 5px;
  margin: 0 0 22px;
}
.wizard-progress i {
  height: 4px;
  background: #e8deef;
  flex: 1;
  border-radius: 8px;
}
.wizard-progress i.on {
  background: var(--violet);
}
fieldset {
  border: 0;
  padding: 0;
  margin: 0;
}
legend {
  font:
    700 23px/1.15 Urbanist,
    sans-serif;
  letter-spacing: -0.025em;
  margin-bottom: 17px;
}
.options {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.option {
  border: 1px solid var(--line);
  border-radius: 11px;
  background: transparent;
  padding: 9px 14px;
  font-size: 12px;
}
.option[aria-pressed='true'] {
  background: #eee5fc;
  border-color: var(--violet);
  box-shadow: inset 0 0 0 1px var(--violet);
}
.field-label {
  display: block;
  font-size: 10px;
  margin: 17px 0 7px;
  color: var(--muted);
}
textarea,
.edit-input {
  width: 100%;
  border: 1px solid var(--line);
  background: #faf6fc;
  border-radius: 10px;
  padding: 11px 12px;
  resize: vertical;
  font-size: 12px;
}
textarea {
  min-height: 65px;
}
.edit-input {
  min-height: 44px;
}
.wizard-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  margin-top: 15px;
}
.wizard-footer .note {
  max-width: 180px;
}
.plan-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0 7px;
}
.plan-head strong {
  font-size: 11px;
}
.plan-head .mono {
  color: var(--violet);
}
.plan-mock .row {
  font-size: 11px;
  padding: 10px 0;
}
.plan-total {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  margin-top: 12px;
  background: #f2ecf8;
  border-radius: 9px;
  padding: 10px;
}
.plan-total b {
  color: var(--violet);
}
.internal {
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  border-top: 1px solid var(--line);
  padding-top: 18px;
}
.internal p {
  font-size: 10px;
  margin-top: 5px;
}
.internal strong {
  font-size: 11px;
}
.doc-paper {
  background: #fffdfb;
  border: 1px solid #e3dbe3;
  border-radius: 4px 4px 18px 4px;
  padding: 23px;
  box-shadow: 4px 5px 0 #e9dfef;
}
.doc-brand {
  font:
    700 18px Urbanist,
    sans-serif;
  letter-spacing: -0.5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.doc-brand span {
  font:
    400 8px 'IBM Plex Mono',
    monospace;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.doc-paper h4 {
  font-size: 29px;
  margin: 24px 0 16px;
}
.doc-paper p {
  font-size: 11px;
  line-height: 1.9;
  min-height: 83px;
}
.doc-paper footer {
  border-top: 1px solid var(--line);
  margin-top: 22px;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  font-size: 8px;
  color: var(--muted);
}
.versions {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 18px;
  flex-wrap: wrap;
}
.versions button {
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 10px;
  min-width: 44px;
  font-size: 11px;
}
.versions button[aria-pressed='true'] {
  background: var(--ink);
  color: white;
  border-color: var(--ink);
}
.versions .restore {
  margin-left: auto;
  padding: 9px 12px;
}
.rewrite {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
  padding: 12px;
  background: #f1eaf8;
  border-radius: 14px;
}
.rewrite > span {
  font-size: 12px;
  flex: 1;
}
.rewrite button {
  background: #fffcff;
  padding: 9px 12px;
  border-radius: 9px;
  font-size: 11px;
}
.document-note {
  margin-top: 13px;
  min-height: 34px;
}
.portal-section {
  padding: 110px 0 85px;
  position: relative;
  isolation: isolate;
}
.portal-section:before {
  content: '';
  position: absolute;
  inset: 290px 0 130px;
  z-index: -1;
  background: radial-gradient(ellipse at 70% 45%, #dbc6fb70, transparent 65%);
}
.portal-heading {
  max-width: 850px;
}
.portal-heading h2 {
  font-size: clamp(44px, 6vw, 78px);
}
.portal-heading p {
  max-width: 480px;
}
.portal-demo {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 70px;
  align-items: start;
  margin-top: 55px;
}
.controls {
  padding: 30px 0;
}
.controls h3 {
  font-size: 29px;
}
.controls > p {
  font-size: 13px;
  margin: 15px 0 25px;
  max-width: 320px;
}
.control-label {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  border-bottom: 1px solid var(--line);
  padding: 10px 0;
  min-height: 65px;
  cursor: pointer;
}
.control-label strong {
  font-size: 13px;
  font-weight: 500;
}
.control-label small {
  display: block;
  font-size: 10px;
  color: var(--muted);
}
.switch {
  position: relative;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  flex: none;
}
.switch input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 1;
}
.switch i {
  width: 44px;
  height: 26px;
  border-radius: 20px;
  background: #cfc5d9;
  position: relative;
  pointer-events: none;
}
.switch i:after {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px #231b3620;
  transition: transform 0.18s var(--ease);
}
.switch input:checked + i {
  background: var(--violet);
}
.switch input:checked + i:after {
  transform: translateX(18px);
}
.switch input:focus-visible + i {
  outline: 3px solid var(--violet);
  outline-offset: 5px;
}
.per-doc {
  padding: 8px 0 8px 17px;
  border-left: 2px solid #e1d4ef;
  margin: 12px 0;
}
.per-doc label {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 44px;
  font-size: 11px;
  cursor: pointer;
}
.per-doc input {
  accent-color: var(--violet);
  width: 17px;
  height: 17px;
}
.per-doc:has(input:disabled) {
  opacity: 0.45;
}
.control-status {
  min-height: 57px;
  margin-top: 20px;
  padding: 12px 14px;
  border: 1px solid #d8c7eb;
  border-radius: 12px;
  background: #f0e8fa;
  color: var(--violet);
  font-size: 11px;
}
.pin-explainer {
  margin-top: 27px;
  border-top: 1px solid var(--line);
  padding-top: 24px;
}
.pin-explainer strong {
  font-size: 12px;
}
.pin-explainer p {
  font-size: 11px;
  line-height: 1.9;
  margin-top: 7px;
}
.preview-stage {
  padding: 22px;
  background: var(--aurora);
  border: 1px solid #d2bde7;
  border-radius: 32px;
  position: relative;
}
.preview-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 4px 16px;
  font-size: 9px;
  color: #583e72;
  letter-spacing: 0.05em;
}
.preview-label .live-dot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.live-dot:before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #166344;
}
.browser {
  background: #fffcff;
  border: 1px solid white;
  border-radius: 19px;
  overflow: hidden;
  box-shadow: 0 15px 40px #56346518;
}
.address {
  padding: 14px 16px;
  background: #f7f2fa;
  border-bottom: 1px solid #eae1f0;
  display: flex;
  align-items: center;
  gap: 8px;
  font:
    400 9px 'IBM Plex Mono',
    monospace;
  letter-spacing: -0.02em;
}
.address svg {
  width: 11px;
  height: 12px;
  flex: none;
}
.address span {
  overflow-wrap: anywhere;
}
.portal-body {
  padding: 25px;
}
.client-brand {
  font:
    800 23px Urbanist,
    sans-serif;
  letter-spacing: -0.8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.client-brand .pill {
  font:
    500 9px 'IBM Plex Mono',
    monospace;
  letter-spacing: 0;
}
.client-header {
  padding: 25px 0 18px;
}
.client-header > p {
  font-size: 10px;
  margin-bottom: 7px;
}
.client-header h3 {
  font-size: 34px;
  margin-bottom: 22px;
}
.client-header .project-progress strong {
  font-size: 42px;
}
.portal-block {
  border-top: 1px solid var(--line);
  padding: 20px 0 3px;
}
.portal-block h4 {
  font:
    600 10px 'IBM Plex Mono',
    monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: var(--violet);
}
.portal-block .checkline {
  font-size: 11px;
}
.task-group {
  font-size: 9px;
  color: var(--muted);
  margin: 10px 0 2px;
}
.event {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 7px 0 13px;
}
.event-date {
  background: #f0e8f9;
  border-radius: 12px;
  padding: 9px 12px;
  text-align: center;
  font-size: 8px;
  line-height: 1.5;
  color: var(--violet);
}
.event-date b {
  font:
    800 27px/1 Urbanist,
    sans-serif;
  display: block;
}
.event strong {
  font-size: 12px;
}
.event p {
  font-size: 10px;
}
.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid #eee6f3;
  gap: 10px;
}
.payment-row strong {
  display: block;
  font-size: 12px;
  font-weight: 600;
}
.payment-row small {
  font-size: 10px;
  color: var(--muted);
}
.payment-total {
  font-size: 10px;
  text-align: right;
  margin: 11px 0;
  color: var(--muted);
}
.pdf {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  min-height: 52px;
}
.pdf-icon {
  border: 1px solid #d9c6ea;
  border-radius: 6px;
  padding: 8px 5px;
  color: var(--violet);
  font:
    500 8px 'IBM Plex Mono',
    monospace;
}
.pdf-name {
  font-size: 11px;
  flex: 1;
}
.download {
  width: 44px;
  justify-content: center;
  border-radius: 9px;
  background: #f3edf8;
  font-size: 19px;
}
.download:hover {
  background: #e9ddf5;
}
.empty-state {
  border: 1px dashed #d7c6e6;
  border-radius: 14px;
  margin-top: 18px;
  padding: 30px 20px;
  text-align: center;
}
.empty-state strong {
  font:
    700 22px Urbanist,
    sans-serif;
  display: block;
  margin: 8px 0;
}
.empty-state p {
  font-size: 11px;
}
.client-footer {
  margin-top: 23px;
  text-align: center;
  font-size: 9px;
  color: var(--muted);
}
.client-footer b {
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  letter-spacing: -0.5px;
}
.preview-caption {
  font-size: 10px;
  margin: 16px 6px 0;
  color: #593e6d;
  text-align: center;
}
.portal-bottom {
  margin-top: 45px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  padding-top: 25px;
  border-top: 1px solid var(--line);
}
.portal-bottom strong {
  font-size: 12px;
}
.portal-bottom p {
  font-size: 11px;
  margin-top: 8px;
  line-height: 1.9;
}
.trust {
  padding: 45px 0 90px;
}
.trust-box {
  border: 1px solid #ded1e9;
  border-radius: 30px;
  padding: 50px;
  background: #eee7f4;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 65px;
  align-items: center;
}
.trust-box h2 {
  font-size: 48px;
}
.trust-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.trust-list li {
  padding: 17px 0;
  border-bottom: 1px solid #d8cce2;
  display: flex;
  gap: 15px;
}
.trust-list li:last-child {
  border-bottom: 0;
}
.trust-list li > span {
  font:
    500 11px 'IBM Plex Mono',
    monospace;
  color: var(--violet);
  padding-top: 3px;
}
.trust-list strong {
  font-size: 13px;
  font-weight: 600;
}
.trust-list p {
  font-size: 11px;
  margin-top: 5px;
}
.closing {
  padding: 95px 0 105px;
  text-align: center;
  position: relative;
  overflow: hidden;
  background: var(--aurora);
  isolation: isolate;
}
.closing:before {
  content: '';
  position: absolute;
  border: 1px solid #ad88c37a;
  border-radius: 50%;
  width: 680px;
  height: 370px;
  left: 50%;
  top: 80px;
  transform: translateX(-50%) rotate(-19deg);
  z-index: -1;
}
.closing .eyebrow {
  justify-content: center;
  color: #61437b;
}
.closing h2 {
  max-width: 780px;
  margin: 0 auto;
}
.closing h2 span {
  color: #65419a;
}
.closing p {
  color: #654a78;
  margin: 23px 0 28px;
}
.closing .cta {
  background: #fffcff;
  border-color: #e4d2ec;
  min-width: 180px;
}
.closing .guarantee {
  color: #654a78;
}
.closing-spark {
  font:
    160px/1 Urbanist,
    sans-serif;
  color: #fff8ff70;
  position: absolute;
  right: 6%;
  top: 25px;
  z-index: -1;
  transform: rotate(12deg);
}
.reveal {
  opacity: 1;
}
.motion .reveal {
  opacity: 0;
  transform: translateY(19px);
  transition:
    opacity 0.65s var(--ease),
    transform 0.65s var(--ease);
}

.motion .reveal:not(.visible):not(.active) .bar i {
  transform: scaleX(0);
}
.bar i {
  transition: transform 0.85s var(--ease);
}
@media (min-width: 1400px) {
  .hero {
    padding-top: 75px;
    padding-bottom: 90px;
  }
}
@media (max-width: 950px) {
  .hero {
    gap: 22px;
  }
  .hero h1 {
    font-size: 67px;
  }
  .hero-project {
    padding: 21px;
  }
  .hero-project h2 {
    font-size: 27px;
  }
  .beat {
    gap: 40px;
  }
  .portal-demo {
    gap: 35px;
  }
  .trust-box {
    gap: 35px;
    padding: 35px;
  }
  .trust-box h2 {
    font-size: 42px;
  }
  .portal-body {
    padding: 22px;
  }
  .preview-stage {
    padding: 16px;
  }
  .pain-grid {
    gap: 20px;
  }
}
@media (max-width: 700px) {
  .hero {
    grid-template-columns: 1fr;
    padding: 30px 0 35px;
    gap: 16px;
  }
  .hero h1 {
    font-size: clamp(52px, 13.8vw, 78px);
  }
  .hero-copy .lead {
    font-size: 14px;
    margin: 24px 0;
    max-width: 350px;
  }
  .eyebrow {
    font-size: 9px;
    letter-spacing: 0.05em;
    margin-bottom: 20px;
  }
  .hero-art {
    padding: 20px 0 0;
    max-width: 440px;
    width: 100%;
    margin: auto;
  }
  .scene {
    height: 360px;
  }
  .scene:before {
    inset: 25px 0 10px;
  }
  .orbit {
    inset: 5px 0;
  }
  .hero-card {
    height: 300px;
    width: 88%;
  }
  .hero-project {
    padding: 20px;
  }
  .hero-project h2 {
    font-size: 29px;
    margin: 18px 0;
  }
  .hero-project .mock-top {
    font-size: 9px;
  }
  .project-metrics {
    margin-top: 14px;
  }
  .project-footer {
    margin-top: 12px;
  }
  .spark {
    font-size: 70px;
    right: 0;
    top: -3px;
  }
  .hand-note {
    font-size: 14px;
    right: 0;
    bottom: 2px;
  }
  .scene-caption {
    margin-top: 8px;
  }
  .section {
    padding: 65px 0;
  }
  .pain {
    padding: 35px 0 55px;
  }
  .pain-intro {
    display: block;
    margin-bottom: 28px;
  }
  .pain-intro h2 {
    font-size: 30px;
    max-width: 310px;
  }
  .pain-intro p {
    margin-top: 17px;
    font-size: 12px;
  }
  .pain-grid {
    grid-template-columns: 1fr 1fr;
    gap: 25px 23px;
  }
  .pain-number {
    font-size: 73px;
    letter-spacing: -4px;
  }
  .pain-item p {
    font-size: 11px;
    margin-top: 9px;
  }
  .pain-end {
    font-size: 12px;
    margin-top: 28px;
  }
  .flow-heading {
    display: block;
    margin-bottom: 32px;
  }
  .flow-heading p {
    margin-top: 20px;
    max-width: 330px;
  }
  .beat {
    grid-template-columns: 1fr;
    gap: 25px;
    padding: 35px 0;
  }
  .beat-copy {
    max-width: 100%;
  }
  .beat-copy h3 {
    max-width: 320px;
    font-size: 34px;
  }
  .beat-num {
    margin-bottom: 15px;
  }
  .beat-copy p {
    margin-top: 17px;
    font-size: 13px;
  }
  .mock {
    padding: 21px;
    border-radius: 22px;
  }
  .beat-copy .aside {
    font-size: 11px;
  }
  .section-head {
    margin-bottom: 30px;
  }
  .portal-section {
    padding: 70px 0 45px;
  }
  .portal-heading h2 {
    font-size: clamp(43px, 11.5vw, 65px);
  }
  .section-head .lead {
    font-size: 14px;
  }
  .portal-demo {
    grid-template-columns: 1fr;
    gap: 25px;
    margin-top: 30px;
  }
  .controls {
    padding: 0;
  }
  .controls h3 {
    font-size: 27px;
  }
  .controls > p {
    margin: 12px 0 15px;
    font-size: 12px;
  }
  .control-label {
    min-height: 61px;
  }
  .per-doc {
    display: flex;
    flex-wrap: wrap;
    gap: 0 14px;
  }
  .pin-explainer {
    margin-top: 15px;
    padding-top: 18px;
  }
  .preview-stage {
    padding: 13px;
    border-radius: 25px;
  }
  .preview-label {
    font-size: 8px;
    margin: 2px 3px 12px;
  }
  .portal-body {
    padding: 20px;
  }
  .address {
    padding: 13px 12px;
    font-size: 8px;
  }
  .client-header h3 {
    font-size: 32px;
  }
  .client-brand {
    font-size: 22px;
  }
  .client-header {
    padding-top: 22px;
  }
  .preview-caption {
    font-size: 9px;
  }
  .portal-bottom {
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 27px;
    padding-top: 22px;
  }
  .portal-bottom p {
    margin-top: 4px;
  }
  .trust {
    padding: 20px 0 60px;
  }
  .trust-box {
    grid-template-columns: 1fr;
    padding: 27px 23px;
    gap: 20px;
    border-radius: 24px;
  }
  .trust-box h2 {
    font-size: 38px;
  }
  .trust-box .eyebrow {
    font-size: 8px;
  }
  .trust-list li {
    padding: 14px 0;
  }
  .closing {
    padding: 65px 0 70px;
  }
  .closing h2 {
    font-size: 44px;
  }
  .closing p {
    font-size: 12px;
    max-width: 270px;
    margin: 22px auto;
  }
  .closing:before {
    width: 470px;
    height: 300px;
    top: 70px;
  }
  .closing-spark {
    font-size: 110px;
    top: 18px;
    right: -20px;
  }
}
@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  *,
  *:before,
  *:after {
    animation: none !important;
    transition: none !important;
  }
  .motion .reveal {
    opacity: 1;
    transform: none;
  }
  .motion .reveal:not(.visible):not(.active) .bar i {
    transform: none;
  }
  .running .approval-toast {
    display: none;
  }
  .running .hero-project {
    opacity: 1;
    transform: none;
  }
}
</style>
